// useAuthRedirect.js
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { rehydrate } from '../reducers/authSlice';

// Routes that render for logged-out users (sign in/up + the password
// reset / legacy activation flows that land from emailed links).
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/activate',
];

const useAuthRedirect = (redirectToWhenLoggedIn = '/', redirectToWhenLoggedOut = '/login') => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLoggedIn, isLoading, hasCheckedRehydrate, errorMessage } = useSelector(state => state.auth);

  useEffect(() => {
    if (!isLoggedIn && !hasCheckedRehydrate) {
      dispatch(rehydrate());
    }
  }, [dispatch, isLoggedIn, hasCheckedRehydrate]);

  useEffect(() => {
    if (!hasCheckedRehydrate || isLoading) return; // Skip redirect while loading or rehydration is in progress

    const isPublicPath = PUBLIC_PATHS.includes(location.pathname);

    if (isLoggedIn) {
      // A logged-in user has no business on the public auth screens.
      if (isPublicPath) {
        navigate(redirectToWhenLoggedIn);
      }
    } else {
      // A logged-out user on a non-public route gets sent to sign in.
      if (!isPublicPath) {
        navigate(redirectToWhenLoggedOut);
      }
    }
  }, [isLoggedIn, isLoading, hasCheckedRehydrate, navigate, location.pathname, redirectToWhenLoggedIn, redirectToWhenLoggedOut]);

  return { isLoading, hasCheckedRehydrate, errorMessage };
};

export default useAuthRedirect;